<template>
  <div>
    <AchBankList
      v-show="!isLinkAccount"
      :key="componentKey"
      :account-id="accountId"
      :account-role="accountRole"
      :charge-amount="chargeAmount"
      :mode="mode"
      :payment-loading="payWithSavedAchLoading"
      :currency="currency"
      :clinic-id="clinicId"
      :customer-id="customerId"
      @clickLinkAccount="isLinkAccount = true"
      @payWithSavedAch="payWithSavedAch"
    />
    <form v-show="isLinkAccount" :id="formId">
      <div :id="`payment-element-${formId}`">
        <!--Stripe.js injects the Payment Element-->
      </div>
      <div v-if="mode === 'setup'" class="flex items-center justify-between mt-5 gap-x-3">
        <NewLibraryButton color-type="dark-blue-stroke" class="w-1/6" @click="isLinkAccount = false">Cancel</NewLibraryButton>
        <button id="submit" v-loading.lock="completePaymentLoading" class="w-full">Add Account</button>
      </div>
      <div v-else class="flex items-center justify-between mt-5">
        <NewLibraryButton color-type="dark-blue-stroke" class="w-1/6" @click="isLinkAccount = false">Back</NewLibraryButton>
        <button id="submit" v-loading.lock="completePaymentLoading" :class="isBottom ? 'bottom-fixed' : 'w-9/12'">
          Complete Payment {{ currency.symbol + chargeAmount.toFixed(2) }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import AchBankList from './AchBankList.vue';
import NewLibraryButton from "@/NewLibrary/Button.vue";
import { getStripeKey, createPaymentIntent, chargeApi, savePaymentApi, payOrdersApi } from '@/api/va-order-page/newChargingService.js';
import { getOrderSamplePriceMaps } from '../..';
import { notify } from '@/utils/notify';
export default {
  components: {
    AchBankList,
    NewLibraryButton,
  },
  props: {
    accountRole: {
      type: String,
      default: 'customer',
    },
    accountId: {
      type: Number,
      default: null,
    },
    chargeAmount: {
      type: Number,
      default: 0,
    },
    mode: {
      type: String,
      default: 'payment',
    },
    sampleList: {
      type: Array,
      default: () => [],
    },
    formId: {
      type: String,
      default: 'payment-form',
    },
    clinicId: {
      type: Number,
      default: null
    },
    currency: {
      type: Object,
      default: () => ({ name: "USD", symbol: "$"})
    },
    orderPriceList: {
      type: Array,
      default: () => [],
    },
    payOrderWithoutApply: {
      type: Boolean,
      default: false
    },
    customerId: {
      type: Number,
      default: null,
    },
  },
  emits: ['completeAchPayment'],
  data() {
    return {
      isLinkAccount: false,
      email: '',
      name: '',
      completePaymentLoading: false,
      savedBankList: [],
      stripeKey: '',
      bankSvg: require('@/assets/orderTest/bank.svg'),
      payWithSavedAchLoading: false,
      componentKey: 0,
    };
  },
  computed: {
    isBottom() {
      return document.location.href.includes('/ordertest') 
        && !document.location.href.includes('order-center');
    },
  },
  mounted() {
    this.initialize();
  },

  methods: {
    async initialize() {
      if (!this.accountId || this.mode === 'display') {
        return;
      }
      // get stripe key
      let stripeKeyResp = await getStripeKey();
      this.stripeKey = stripeKeyResp.data.stripe_key;
      const stripe = window.Stripe(stripeKeyResp.data.stripe_key);
      // let elements = stripe.elements({ appearance, clientSecret });
      const options = {
        mode: this.mode,
        amount: Number((this.chargeAmount * 100).toFixed(2)),
        currency: this.currency.name.toLowerCase(),
        // Fully customizable with appearance API.
        appearance: {
          theme: 'stripe',
        },
        paymentMethodOptions: {
          us_bank_account: {
            verification_method: 'automatic',
          },
        },
        paymentMethodTypes: ['us_bank_account'],
        paymentMethodCreation: 'manual',
      };
      let elements = stripe.elements(options);
      const paymentElementOptions = {
        layout: 'tabs',
        // terms: {
        //   usBankAccount: "never"
        // }
      };

      const paymentElement = elements.create('payment', paymentElementOptions);
      paymentElement.mount(`#payment-element-${this.formId}`);

      let paymentForm = document.querySelector(`#${this.formId}`);
      paymentForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        try {
          this.completePaymentLoading = true;
          // call stripe submit payment elements before create payment method api
          const submitResp = await elements.submit();
          if (submitResp.error) {
            this.completePaymentLoading = false;
            notify({ type: 'error', title: 'Failed', message: submitResp.error });
            return;
          }
          // call stripe create payment method to get payment token
          const createPaymentMethodResp = await stripe.createPaymentMethod({ elements });
          if (createPaymentMethodResp.error) {
            this.completePaymentLoading = false;
            notify({ type: 'error', title: 'Failed', message: createPaymentMethodResp.error });
            return;
          }
          let paymentToken = createPaymentMethodResp.paymentMethod.id;
          let achPaymentInfo = null;
          if (this.mode === 'payment') {
            let orderSamplePriceMaps = await getOrderSamplePriceMaps(this.orderPriceList);
            if (this.chargeAmount === 0) {
              this.$emit('completeAchPayment', {
                paymentId: null,
                sampleId: null,
                julienBarcode: null,
                orderSamplePriceMaps
              });
              return;
            }
            // call charge api in payment mode
            let recordId = await this.getRecordId();
            achPaymentInfo = await this.getAchPaymentInfo(stripe, paymentToken, recordId, orderSamplePriceMaps);
            if (achPaymentInfo.nextAction || achPaymentInfo.error) {
              let { error, nextAction } = achPaymentInfo;
              let title = nextAction ? "Payment Authentication Required" : "Failed";
              let message = error ? error : "We're sorry, but we encountered an issue processing your payment. Please try again in a few minutes or use a different bank account. If the problem persists, contact your bank or our support team.";
              notify({ type: 'error', title, message });
              this.completePaymentLoading = false;
              return;
            }
            // save ach payment method
            let { us_bank_account } = createPaymentMethodResp.paymentMethod;
            await this.callSaveAchPaymentApi(paymentToken, us_bank_account);
            this.$emit('completeAchPayment', achPaymentInfo);
          } else { // other modes
            // save ach payment method
            let { us_bank_account } = createPaymentMethodResp.paymentMethod;
            await this.callSaveAchPaymentApi(paymentToken, us_bank_account);
            this.componentKey++; // refresh ach bank list to show new saved ach payment method
            this.isLinkAccount = false;
          }
          this.completePaymentLoading = false;
        } catch (err) {
          console.log(err);
          this.completePaymentLoading = false;
        }
      });
    },
    async getRecordId() {
      let recordId = null;
      // create payment intent to get record id
      let paymentBody = {
        amount: Number(this.chargeAmount.toFixed(2)),
        currency: this.currency.name.toLowerCase(),
        payment_type: 'ach',
      };
      let paymentIntentResp = await createPaymentIntent(paymentBody);
      recordId = paymentIntentResp.data.record_id;
      return recordId;
    },
    async payWithSavedAch(bank) {
      let orderSamplePriceMaps = { sampleIdJulienBarcodeMap: {}, samplePriceMap: {} };
      orderSamplePriceMaps = await getOrderSamplePriceMaps(this.orderPriceList);
      if (this.chargeAmount === 0) {
        this.$emit('completeAchPayment', {
          paymentId: null,
          sampleId: null,
          julienBarcode: null,
          orderSamplePriceMaps
        });
        return;
      }
      try {
        this.payWithSavedAchLoading = true;
        const stripe = window.Stripe(this.stripeKey);
        let recordId = await this.getRecordId();
        let achPaymentInfo = await this.getAchPaymentInfo(stripe, bank.payment_token, recordId, orderSamplePriceMaps);
        if (achPaymentInfo.nextAction || achPaymentInfo.error) {
          let { error, nextAction } = achPaymentInfo;
          let title = nextAction ? "Payment Authentication Required" : "Failed";
          let message = error ? error : "We're sorry, but we encountered an issue processing your payment. Please try again in a few minutes or use a different bank account. If the problem persists, contact your bank or our support team.";
          notify({ type: 'error', title, message });
          this.payWithSavedAchLoading = false;
          return;
        }
        this.payWithSavedAchLoading = false;
        this.$emit('completeAchPayment', achPaymentInfo);
      } catch (err) {
        console.log('pay with saved ach error', err);
        this.payWithSavedAchLoading = false;
      }
    },
    async getAchPaymentInfo(stripe, paymentToken, recordId, orderSamplePriceMaps) {
      try {
        let samples = [];
        if (this.sampleList.length > 0) {
          samples = this.sampleList;
        } else {
          let { sampleIdJulienBarcodeMap, samplePriceMap } = orderSamplePriceMaps;
          Object.keys(sampleIdJulienBarcodeMap).forEach(sampleId => {
            samples.push({
              orderId: null,
              sample_id: sampleId,
              balance: samplePriceMap[sampleId]
            });
          });
        }
        let achChargeResp = await this.callPayOrdersApi(paymentToken, recordId, samples);
        let { error } = await stripe.confirmUsBankAccountPayment(achChargeResp.data.client_secret);
        if (error) {
          return { error: error.message };
        }
        let { sample_id, julien_barcode, record_id, next_action } = achChargeResp.data;
        this.completePaymentLoading = false;
        let achPaymentInfo = {
          paymentId: record_id,
          sampleId: sample_id,
          julienBarcode: julien_barcode,
          nextAction: next_action,
          amount: Number(this.chargeAmount.toFixed(2)),
          type: "ach",
          platform: "stripe",
          orderSamplePriceMaps
        };
        return achPaymentInfo;
      } catch (err) {
        console.log('get ach payment info error', err);
      }
    },
    async callAchChargeApi(paymentToken, recordId) {
      let achChargeResp = await chargeApi({
        account_id: this.accountId,
        account_type: this.accountRole,
        amount: Number(this.chargeAmount.toFixed(2)),
        currency: this.currency.name.toLowerCase(),
        using_default: false,
        saving_for_later: true,
        type: 'ach',
        token_platform: 'stripe',
        payment_token: paymentToken,
        notes: '',
        no_retry: false,
        record_id: recordId,
        new_sample: true,
        clinic_id: this.clinicId
      });
      return achChargeResp;
    },

    async callPayOrdersApi(paymentToken, recordId, sampleList) {
      let chargeDetails = [];
      sampleList.forEach((sample) => {
        chargeDetails.push({
          charge_type: 'testorder',
          charge_type_id: sample.orderId,
          sample_id: Number(sample.sample_id),
          price: Number(sample.balance),
        });
      });
      let payOrdersResp = await payOrdersApi({
        account_id: this.accountId,
        account_type: this.accountRole,
        amount: Number(this.chargeAmount.toFixed(2)),
        currency: this.currency.name.toLowerCase(),
        charge_details: chargeDetails,
        type: 'ach',
        token_platform: 'stripe',
        payment_token: paymentToken,
        payment_source: 'portal',
        record_id: recordId,
        without_apply: this.payOrderWithoutApply
      });
      return payOrdersResp;
    },
    async callSaveAchPaymentApi(paymentToken, bankAccount) {
      let saveAchPaymentResp = await savePaymentApi({
        account_id: this.accountId,
        account_type: this.accountRole,
        type: 'ach',
        clinic_id: this.clinicId,
        token_platform: 'stripe',
        payment_token: paymentToken,
        is_default: false,
        last_four: bankAccount.last4,
        bank_name: bankAccount.bank_name,
        routing_number: bankAccount.routing_number,
        bank_account_type: bankAccount.account_type,
        nick_name: '',
        is_subscription: false
      });
      return saveAchPaymentResp;
    },
  },
};
</script>

<style scoped>
#submit {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: 40px;
  padding: 8px 32px;
  border-radius: 4px;
  border: none;
  background: var(--VibrantDarkBlue, #004879);
  color: #ffffff;
  font-family: "IBM Plex Sans Hebrew", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.25px;
  transition: background-color 0.5s ease, color 0.5s ease;
  cursor: pointer;
}

#submit:hover {
  background: #20A7C1;
}

#submit:active {
  background: #5587BC;
}

.bottom-fixed {
  position: fixed;
  bottom: 89px;
  z-index: 1000;
  width: 373px;
  height: 40px;
  right: 52px;
}
</style>